import React, { useCallback, useEffect, useState } from 'react';
import { TouchableOpacity } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useTranslation } from 'react-i18next';
import { Menu, MenuItem, MenuItemLabel } from '@/components/gluestack/menu';
import { Icon, CheckIcon } from '@/components/gluestack/icon';
import { Box } from '@/components/gluestack/box';
import { HStack } from '@/components/gluestack/hstack';
import { Text } from '@/components/gluestack/text';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { RadiusIcon } from '@hugeicons-pro/core-stroke-rounded';
import { useMeasurementPreferences } from '@/components/providers/MeasurementsProvider';

const RADIUS_STORAGE_KEY = '@local_feed_radius';
const DEFAULT_RADIUS_KM = 160;

interface RadiusOption {
  km: number;
  mi: number;
}

const RADIUS_OPTIONS: RadiusOption[] = [
  { km: 15, mi: 10 },
  { km: 80, mi: 50 },
  { km: 160, mi: 100 },
  { km: 400, mi: 250 },
  { km: 750, mi: 500 },
];

interface IRadiusFilterMenuProps {
  radiusKm: number;
  onRadiusChange: (radiusKm: number) => void;
}

export function useLocalFeedRadius() {
  const [radiusKm, setRadiusKmState] = useState(DEFAULT_RADIUS_KM);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const stored = await AsyncStorage.getItem(RADIUS_STORAGE_KEY);
        if (stored) {
          const parsed = parseInt(stored, 10);
          if (!isNaN(parsed)) setRadiusKmState(parsed);
        }
      } catch (e) {
        console.error('Failed to load radius preference', e);
      } finally {
        setIsLoaded(true);
      }
    })();
  }, []);

  const setRadiusKm = useCallback(async (km: number) => {
    setRadiusKmState(km);
    try {
      await AsyncStorage.setItem(RADIUS_STORAGE_KEY, km.toString());
    } catch (e) {
      console.error('Failed to save radius preference', e);
    }
  }, []);

  return { radiusKm, setRadiusKm, isLoaded };
}

export function RadiusFilterMenu({ radiusKm, onRadiusChange }: IRadiusFilterMenuProps) {
  const { t } = useTranslation();
  const { distanceUnit } = useMeasurementPreferences();
  const isMetric = distanceUnit === 'km';

  const currentOption = RADIUS_OPTIONS.find((o) => o.km === radiusKm) ?? RADIUS_OPTIONS[2];
  const displayValue = isMetric
    ? `${currentOption.km} ${t('feeds.radius.km', 'km')}`
    : `${currentOption.mi} ${t('feeds.radius.mi', 'mi')}`;

  return (
    <Menu
      placement="bottom left"
      offset={8}
      trigger={(props: any) => (
        <TouchableOpacity activeOpacity={0.7} {...props} className="select-none">
          <HStack className="items-stretch">
            <Box className="z-10 items-center justify-center rounded-full bg-primary-500 px-2 py-1.5 min-w-7">
              <HugeIconsWrapper icon={RadiusIcon} size={16} className="text-white" />
            </Box>
            <Box className="-ml-4 justify-center rounded-r-full rounded-l-none bg-primary-300 px-2 py-1.5 pl-5 pr-2">
              <Text size="xs" className="text-primary-700 px-1 uppercase">
                {displayValue}
              </Text>
            </Box>
          </HStack>
        </TouchableOpacity>
      )}>
      {/* Title */}
      <MenuItem
        key="title"
        textValue={t('feeds.radius.title', 'Radius')}
        disabled
        className="bg-transparent data-[hover=true]:bg-transparent data-[active=true]:bg-transparent data-[focus=true]:bg-transparent">
        <MenuItemLabel className="text-sm font-bold text-typography-500 uppercase">
          {t('feeds.radius.title', 'Radius')}
        </MenuItemLabel>
      </MenuItem>

      {RADIUS_OPTIONS.map((option) => {
        const label = isMetric
          ? `${option.km} ${t('feeds.radius.km', 'km')}`
          : `${option.mi} ${t('feeds.radius.mi', 'mi')}`;
        const isSelected = option.km === radiusKm;

        return (
          <MenuItem
            key={option.km}
            textValue={label}
            onPress={() => onRadiusChange(option.km)}>
            <MenuItemLabel>{label}</MenuItemLabel>
            {isSelected && <Icon as={CheckIcon} size="sm" className="ml-2" />}
          </MenuItem>
        );
      })}
    </Menu>
  );
}
